import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";

import { PassAlgarveLogo } from "@/components/PassAlgarveLogo";
import Seo from "@/components/Seo";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t, language } = useLanguage();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!acceptedTerms) {
      toast.error(language === "en" ? "You must accept the Terms and Conditions to register." : "Deve aceitar os Termos e Condições para se registar.");
      return;
    }

    if (password !== confirmPassword) {
      toast.error(t("authx.passwordsMismatch"));
      return;
    }

    if (password.length < 6) {
      toast.error(t("authx.passwordMin"));
      return;
    }

    setIsLoading(true);

    try {
      const user = await register(email, password, name);
      toast.success(`${t("authx.welcome")}, ${user.name}! ${t("authx.accountCreated")}`);
      navigate("/dashboard");
    } catch (error) {
      console.error("Register error:", error);
      toast.error(error.response?.data?.detail || t("authx.registerError"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-dark flex flex-col">
      <Seo noindex title="Criar Conta" />
      <div className="flex-1 flex items-center justify-center p-4 relative">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url(https://customer-assets.emergentagent.com/job_algarve-pass-portal/artifacts/ejhtb72b_FOTO_02.png)`
          }}
        />

        <motion.div
          className="w-full max-w-md relative z-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="text-white/70 hover:text-white mb-6 -ml-2"
          data-testid="back-home-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t("authx.back")}
        </Button>

        <Card className="bg-navy border-gold/20 shadow-2xl">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <PassAlgarveLogo size="default" />
            </div>
            <CardTitle className="font-heading text-2xl text-white">{t("authx.registerTitle")}</CardTitle>
            <CardDescription className="text-white/60 font-body">
              {t("authx.registerSubtitle")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-white/80 font-body">{t("authx.fullName")}</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder={t("authx.namePlaceholder")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="bg-navy-dark border-gold/30 text-white placeholder:text-white/40 focus:border-gold focus:ring-gold/20"
                  data-testid="register-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/80 font-body">{t("authx.email")}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t("authx.emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-navy-dark border-gold/30 text-white placeholder:text-white/40 focus:border-gold focus:ring-gold/20"
                  data-testid="register-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-white/80 font-body">{t("authx.password")}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder={t("authx.minChars")}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-navy-dark border-gold/30 text-white placeholder:text-white/40 focus:border-gold focus:ring-gold/20 pr-10"
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-white/80 font-body">{t("authx.confirmPassword")}</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  placeholder={t("authx.repeatPassword")}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="bg-navy-dark border-gold/30 text-white placeholder:text-white/40 focus:border-gold focus:ring-gold/20"
                  data-testid="register-confirm-password-input"
                />
              </div>

              <div className="flex items-start gap-3 pt-2">
                <input
                  id="acceptTerms"
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="accent-[#D4A85D] w-4 h-4 mt-0.5 cursor-pointer flex-shrink-0"
                  data-testid="register-terms-checkbox"
                />
                <label htmlFor="acceptTerms" className="text-white/70 font-body text-sm leading-snug cursor-pointer">
                  {language === "en" ? "I have read and accept the " : "Li e aceito os "}
                  <Link
                    to={language === "en" ? "/terms" : "/termos"}
                    target="_blank"
                    className="text-gold hover:text-gold-light underline"
                    data-testid="register-terms-link"
                  >
                    {language === "en" ? "Terms and Conditions" : "Termos e Condições"}
                  </Link>
                </label>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gold hover:bg-gold-dark text-white font-semibold py-5 rounded-full mt-6"
                data-testid="register-submit-btn"
              >
                {isLoading ? t("authx.creating") : t("authx.registerTitle")}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-white/60 font-body text-sm">
                {t("authx.haveAccount")}{" "}
                <Link to="/login" className="text-gold hover:text-gold-light transition-colors" data-testid="login-link">
                  {t("authx.enter")}
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
      </div>
    </div>
  );
}
